import { useMutation, useQueryClient } from 'react-query'
import { getErrorMessage } from '@hapana/shared/web/services/gqty'
import { stripGQtyError } from './client-packages.utils'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { useTranslation } from 'react-i18next'
import {
  CLIENT_SERVICES_QUERY_KEY,
  SERVICE_CREDIT_SUMMARY_PERIODS_QUERY_KEY,
  SERVICE_CREDIT_ADJUSTMENT_NOTES_QUERY_KEY,
} from '../graphql/client-packages.queries'
import {
  adjustServiceCredits,
  adjustPackageExpiry,
  suspendService,
  resumeService,
  cancelService,
  restoreService,
  cancelScheduledSuspension,
  cancelScheduledCancellation,
  changeMembershipPlan,
  cancelScheduledPlanChange,
} from '../graphql/client-packages.service'

interface UseAdjustCreditsArgs {
  onSuccess?: (ledgerId: string) => void
}

interface UseSuspendServiceArgs {
  onSuccess?: () => void
}

interface UseCancelServiceArgs {
  onSuccess?: (serviceCancellationId?: string | null) => void
}

interface UseRestoreServiceArgs {
  onSuccess?: () => void
}

export const useAdjustCredits = ({ onSuccess }: UseAdjustCreditsArgs = {}) => {
  const { t } = useTranslation('core')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (args: {
      serviceId: string
      siteId: string
      amount: number
      direction: 'IN' | 'OUT'
      notes?: string
      reason?: string
      siteSessionTypeId?: string
      periodId?: string
    }) =>
      adjustServiceCredits({
        input: {
          serviceId: args.serviceId,
          siteId: args.siteId,
          amount: args.amount,
          direction: args.direction,
          notes: args.notes,
          reason: args.reason,
          siteSessionTypeId: args.siteSessionTypeId,
          periodId: args.periodId,
        },
      }),
    {
      onSuccess: (data) => {
        openToast({
          title: t('clients.packages.success.creditsAdjusted'),
          severity: 'success',
        })
        queryClient.invalidateQueries([CLIENT_SERVICES_QUERY_KEY])
        queryClient.invalidateQueries([SERVICE_CREDIT_SUMMARY_PERIODS_QUERY_KEY])
        queryClient.invalidateQueries([SERVICE_CREDIT_ADJUSTMENT_NOTES_QUERY_KEY])
        onSuccess?.((data as any)?.adjustServiceCredits)
      },
      onError: (error: Error) => {
        openToast({
          title: t('clients.packages.errors.adjustCreditsFailed'),
          description: stripGQtyError(getErrorMessage(error, t('clients.packages.errors.update'))),
          severity: 'error',
        })
      },
    }
  )

  return {
    adjustCredits: mutation.mutateAsync,
    isAdjusting: mutation.isLoading,
  }
}

export const useSuspendService = ({ onSuccess }: UseSuspendServiceArgs = {}) => {
  const { t } = useTranslation('core')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (args: {
      serviceId: string
      startDate?: string
      endDate?: string
      reason?: string
      chargeMode?: string
      chargeAmount?: string
      bookingIds?: string[]
      failedInvoiceIds?: string[]
      siteId: string
    }) => suspendService(args),
    {
      onSuccess: () => {
        openToast({
          title: t('clients.packages.success.serviceSuspended'),
          severity: 'success',
        })
        queryClient.invalidateQueries([CLIENT_SERVICES_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error: Error) => {
        openToast({
          title: t('clients.packages.errors.suspendServiceFailed'),
          description: getErrorMessage(error, t('clients.packages.errors.freeze')),
          severity: 'error',
        })
      },
    }
  )

  return {
    suspendService: mutation.mutateAsync,
    isSuspending: mutation.isLoading,
  }
}

export const useResumeService = ({ onSuccess }: UseSuspendServiceArgs = {}) => {
  const { t } = useTranslation('core')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (args: { serviceId: string; notes?: string; siteId: string }) => resumeService(args),
    {
      onSuccess: () => {
        openToast({
          title: t('clients.packages.success.serviceResumed'),
          severity: 'success',
        })
        queryClient.invalidateQueries([CLIENT_SERVICES_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error: Error) => {
        openToast({
          title: t('clients.packages.errors.resumeServiceFailed'),
          description: getErrorMessage(error, t('clients.packages.errors.unfreeze')),
          severity: 'error',
        })
      },
    }
  )

  return {
    resumeService: mutation.mutateAsync,
    isResuming: mutation.isLoading,
  }
}

export const useCancelService = ({ onSuccess }: UseCancelServiceArgs = {}) => {
  const { t } = useTranslation('core')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (args: {
      serviceId: string
      reason?: string
      cancelAt?: string
      bookingIds?: string[]
      failedInvoiceIds?: string[]
      siteId: string
    }) => cancelService(args),
    {
      onSuccess: (data) => {
        const serviceCancellationId = (data as any)?.cancelService
        if (serviceCancellationId) {
          openToast({
            title: t('clients.packages.success.serviceCancellationScheduled'),
            severity: 'success',
          })
        } else {
          openToast({
            title: t('clients.packages.success.serviceCanceled'),
            severity: 'success',
          })
        }
        queryClient.invalidateQueries([CLIENT_SERVICES_QUERY_KEY])
        onSuccess?.(serviceCancellationId)
      },
      onError: (error: Error) => {
        openToast({
          title: t('clients.packages.errors.cancelServiceFailed'),
          description: getErrorMessage(error, t('clients.packages.errors.update')),
          severity: 'error',
        })
      },
    }
  )

  return {
    cancelService: mutation.mutateAsync,
    isCanceling: mutation.isLoading,
  }
}

export const useRestoreService = ({ onSuccess }: UseRestoreServiceArgs = {}) => {
  const { t } = useTranslation('core')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (args: { serviceId: string; notes?: string; siteId: string }) => restoreService(args),
    {
      onSuccess: () => {
        openToast({
          title: t('clients.packages.success.serviceRestored'),
          severity: 'success',
        })
        queryClient.invalidateQueries([CLIENT_SERVICES_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error: Error) => {
        openToast({
          title: t('clients.packages.errors.restoreServiceFailed'),
          description: getErrorMessage(error, t('clients.packages.errors.update')),
          severity: 'error',
        })
      },
    }
  )

  return {
    restoreService: mutation.mutateAsync,
    isRestoring: mutation.isLoading,
  }
}

interface UseCancelScheduledSuspensionArgs {
  onSuccess?: () => void
}

export const useCancelScheduledSuspension = ({
  onSuccess,
}: UseCancelScheduledSuspensionArgs = {}) => {
  const { t } = useTranslation('core')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (args: { serviceSuspensionId: string; reason?: string; siteId: string }) =>
      cancelScheduledSuspension(args),
    {
      onSuccess: () => {
        openToast({
          title: t('clients.packages.success.scheduledSuspensionCanceled'),
          severity: 'success',
        })
        queryClient.invalidateQueries([CLIENT_SERVICES_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error: Error) => {
        openToast({
          title: t('clients.packages.errors.cancelScheduledSuspensionFailed'),
          description: getErrorMessage(error, t('clients.packages.errors.update')),
          severity: 'error',
        })
      },
    }
  )

  return {
    cancelScheduledSuspension: mutation.mutateAsync,
    isCanceling: mutation.isLoading,
  }
}

interface UseCancelScheduledCancellationArgs {
  onSuccess?: () => void
}

export const useCancelScheduledCancellation = ({
  onSuccess,
}: UseCancelScheduledCancellationArgs = {}) => {
  const { t } = useTranslation('core')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (args: { serviceCancellationId: string; reason?: string }) => cancelScheduledCancellation(args),
    {
      onSuccess: () => {
        openToast({
          title: t('clients.packages.success.scheduledCancellationCanceled'),
          severity: 'success',
        })
        queryClient.invalidateQueries([CLIENT_SERVICES_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error: Error) => {
        openToast({
          title: t('clients.packages.errors.cancelScheduledCancellationFailed'),
          description: getErrorMessage(error, t('clients.packages.errors.update')),
          severity: 'error',
        })
      },
    }
  )

  return {
    cancelScheduledCancellation: mutation.mutateAsync,
    isCanceling: mutation.isLoading,
  }
}

interface UseAdjustPackageExpiryArgs {
  onSuccess?: () => void
}

export const useAdjustPackageExpiry = ({ onSuccess }: UseAdjustPackageExpiryArgs = {}) => {
  const { t } = useTranslation('core')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (args: { serviceId: string; expiresAtDate: string; siteId: string }) =>
      adjustPackageExpiry(args),
    {
      onSuccess: () => {
        openToast({
          title: t('clients.packages.success.expiryUpdated'),
          severity: 'success',
        })
        queryClient.invalidateQueries([CLIENT_SERVICES_QUERY_KEY])
        queryClient.invalidateQueries([SERVICE_CREDIT_SUMMARY_PERIODS_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error: Error) => {
        openToast({
          title: t('clients.packages.errors.adjustExpiryFailed'),
          description: getErrorMessage(error, t('clients.packages.errors.update')),
          severity: 'error',
        })
      },
    }
  )

  return {
    adjustPackageExpiry: mutation.mutateAsync,
    isAdjusting: mutation.isLoading,
  }
}

export const useChangeMembershipPlan = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const { t } = useTranslation('core')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (args: {
      siteId: string
      serviceId: string
      toServiceTemplateId: string
      effectiveType: 'Immediate' | 'EndOfCycle'
      reason?: string
      signedClientAgreementId?: string
    }) =>
      changeMembershipPlan({
        input: {
          siteId: args.siteId,
          serviceId: args.serviceId,
          toServiceTemplateId: args.toServiceTemplateId,
          effectiveType: args.effectiveType,
          reason: args.reason,
          signedClientAgreementId: args.signedClientAgreementId,
        },
      }),
    {
      onSuccess: () => {
        openToast({
          title: t('clients.packages.success.planChanged'),
          severity: 'success',
        })
        queryClient.invalidateQueries([CLIENT_SERVICES_QUERY_KEY])
        queryClient.invalidateQueries([SERVICE_CREDIT_SUMMARY_PERIODS_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error: Error) => {
        openToast({
          title: t('clients.packages.errors.changePlanFailed'),
          description: error.message,
          severity: 'error',
        })
      },
    }
  )

  return {
    changeMembershipPlan: mutation.mutateAsync,
    isChangingPlan: mutation.isLoading,
  }
}

export const useCancelScheduledPlanChange = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const { t } = useTranslation('core')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    (args: { servicePlanChangeId: string; siteId: string }) =>
      cancelScheduledPlanChange({
        input: { servicePlanChangeId: args.servicePlanChangeId, siteId: args.siteId },
      }),
    {
      onSuccess: () => {
        openToast({
          title: t('clients.packages.success.scheduledPlanChangeCanceled'),
          severity: 'success',
        })
        queryClient.invalidateQueries([CLIENT_SERVICES_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error: Error) => {
        openToast({
          title: t('clients.packages.errors.cancelScheduledPlanChangeFailed'),
          description: error.message,
          severity: 'error',
        })
      },
    }
  )

  return {
    cancelScheduledPlanChange: mutation.mutateAsync,
    isCanceling: mutation.isLoading,
  }
}
